/**
 * ============================================================================
 * kbBlockCatalog — Catálogo único de blocos inseríveis (usado pelo slash menu
 *                  "/" e pelo popover do handle "+" ao lado de cada bloco,
 *                  padrão GitBook/Notion).
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial (Bloco Handle GitBook).
 * ============================================================================
 */
import type { Editor, Range } from "@tiptap/core";
import {
  Heading1, Heading2, Heading3, List, ListOrdered, ListChecks,
  Quote, Code2, Table as TableIcon, Image as ImageIcon,
  AlertTriangle, ListStart, ChevronsUpDown, LayoutGrid, Youtube, Minus,
  Type, PanelTop, Link2,
} from "lucide-react";
import { openPageLinkPicker } from "@/lib/kb/pageLinkPicker";


/** Grupo visual do bloco no popover/slash menu. */
export type BlockGroup = "basic" | "advanced";

export interface BlockCatalogItem {
  /** Chave estável (usada em keys de lista). */
  key: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  keywords: string[];
  group: BlockGroup;
  /**
   * Comando que INSERE o bloco. Recebe `editor` e um `range` OPCIONAL
   * (usado pelo slash menu para apagar o "/"). Quando chamado pelo
   * handle "+", o `range` é `null` e a inserção acontece no `pos` atual
   * do bloco alvo (o caller passa esse pos via `editor.chain().focus()`).
   */
  command: (props: { editor: Editor; range?: Range | null }) => void;
}

/** Helper: aplica deleteRange quando vier do slash menu. */
function withRange(chain: any, range?: Range | null) {
  return range ? chain.deleteRange(range) : chain;
}

/**
 * JSON canônico da tabela padrão inserida no KB (estilo GitBook):
 * grid 3x3 com cabeçalhos "Text" e duas linhas de células vazias.
 * Usado pelo slash menu, catálogo e toolbar para manter consistência.
 */
export const KB_DEFAULT_TABLE_JSON = {
  type: "table",
  content: [
    {
      type: "tableRow",
      content: [
        { type: "tableHeader", content: [{ type: "paragraph", content: [{ type: "text", text: "Text" }] }] },
        { type: "tableHeader", content: [{ type: "paragraph", content: [{ type: "text", text: "Text" }] }] },
        { type: "tableHeader", content: [{ type: "paragraph", content: [{ type: "text", text: "Text" }] }] },
      ],
    },
    {
      type: "tableRow",
      content: [
        { type: "tableCell", content: [{ type: "paragraph" }] },
        { type: "tableCell", content: [{ type: "paragraph" }] },
        { type: "tableCell", content: [{ type: "paragraph" }] },
      ],
    },
    {
      type: "tableRow",
      content: [
        { type: "tableCell", content: [{ type: "paragraph" }] },
        { type: "tableCell", content: [{ type: "paragraph" }] },
        { type: "tableCell", content: [{ type: "paragraph" }] },
      ],
    },
  ],
} as const;

export const BLOCK_CATALOG: BlockCatalogItem[] = [
  // ------------------- BÁSICOS -------------------
  {
    key: "paragraph",
    title: "Parágrafo",
    description: "Texto simples",
    icon: Type,
    keywords: ["p", "paragrafo", "texto"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).setNode("paragraph").run(),
  },
  {
    key: "h1",
    title: "Título 1",
    description: "Cabeçalho grande",
    icon: Heading1,
    keywords: ["h1", "titulo", "cabecalho"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).setNode("heading", { level: 1 }).run(),
  },
  {
    key: "h2",
    title: "Título 2",
    description: "Cabeçalho médio",
    icon: Heading2,
    keywords: ["h2", "titulo"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).setNode("heading", { level: 2 }).run(),
  },
  {
    key: "h3",
    title: "Título 3",
    description: "Cabeçalho pequeno",
    icon: Heading3,
    keywords: ["h3", "titulo"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).setNode("heading", { level: 3 }).run(),
  },
  {
    key: "bulletList",
    title: "Lista",
    description: "Bullets",
    icon: List,
    keywords: ["lista", "bullet", "ul"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).toggleBulletList().run(),
  },
  {
    key: "orderedList",
    title: "Lista numerada",
    description: "1. 2. 3.",
    icon: ListOrdered,
    keywords: ["numerada", "ol"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).toggleOrderedList().run(),
  },
  {
    key: "taskList",
    title: "Checklist",
    description: "Tarefas com caixa marcável",
    icon: ListChecks,
    keywords: ["task", "checklist", "todo"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).toggleTaskList().run(),
  },
  {
    key: "quote",
    title: "Citação",
    description: "Blockquote",
    icon: Quote,
    keywords: ["quote", "citacao", "blockquote"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).toggleBlockquote().run(),
  },
  {
    key: "codeBlock",
    title: "Bloco de código",
    description: "Trecho monoespaçado",
    icon: Code2,
    keywords: ["code", "codigo", "snippet"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).toggleCodeBlock().run(),
  },
  {
    key: "divider",
    title: "Divisor",
    description: "Linha horizontal",
    icon: Minus,
    keywords: ["hr", "divisor", "separador"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).setHorizontalRule().run(),
  },
  {
    key: "callout-info",
    title: "Destaque (Info)",
    description: "Callout azul",
    icon: AlertTriangle,
    keywords: ["callout", "info", "hint", "destaque"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).insertContent({
        type: "callout",
        attrs: { variant: "info" },
        content: [{ type: "paragraph", content: [{ type: "text", text: "Escreva sua nota aqui…" }] }],
      }).run(),
  },
  {
    key: "callout-success",
    title: "Destaque (Sucesso)",
    description: "Callout verde",
    icon: AlertTriangle,
    keywords: ["callout", "sucesso"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).insertContent({
        type: "callout",
        attrs: { variant: "success" },
        content: [{ type: "paragraph", content: [{ type: "text", text: "Dica…" }] }],
      }).run(),
  },
  {
    key: "callout-warning",
    title: "Destaque (Aviso)",
    description: "Callout amarelo",
    icon: AlertTriangle,
    keywords: ["callout", "aviso", "warning"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).insertContent({
        type: "callout",
        attrs: { variant: "warning" },
        content: [{ type: "paragraph", content: [{ type: "text", text: "Atenção…" }] }],
      }).run(),
  },
  {
    key: "callout-danger",
    title: "Destaque (Perigo)",
    description: "Callout vermelho",
    icon: AlertTriangle,
    keywords: ["callout", "danger", "perigo"],
    group: "basic",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).insertContent({
        type: "callout",
        attrs: { variant: "danger" },
        content: [{ type: "paragraph", content: [{ type: "text", text: "Cuidado…" }] }],
      }).run(),
  },

  // ------------------- AVANÇADOS -------------------
  {
    key: "table",
    title: "Tabela",
    description: "Grid 3x3 com cabeçalho",
    icon: TableIcon,
    keywords: ["tabela", "table"],
    group: "advanced",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).insertContent(KB_DEFAULT_TABLE_JSON as any).run(),
  },
  {
    key: "cardGrid",
    title: "Cards",
    description: "Grade de cartões navegáveis",
    icon: LayoutGrid,
    keywords: ["card", "cards", "grade"],
    group: "advanced",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).insertContent({
        type: "cardGrid",
        content: [
          { type: "card", attrs: { title: "Cartão 1", description: "Descrição do cartão", href: "#" } },
          { type: "card", attrs: { title: "Cartão 2", description: "Descrição do cartão", href: "#" } },
        ],
      }).run(),
  },
  {
    key: "steps",
    title: "Steps",
    description: "Passo a passo numerado",
    icon: ListStart,
    keywords: ["steps", "passos", "stepper", "tutorial"],
    group: "advanced",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).insertContent({
        type: "steps",
        content: [
          { type: "stepItem", content: [{ type: "paragraph", content: [{ type: "text", text: "Primeiro passo…" }] }] },
          { type: "stepItem", content: [{ type: "paragraph", content: [{ type: "text", text: "Segundo passo…" }] }] },
        ],
      }).run(),
  },
  {
    key: "toggle",
    title: "Expansível",
    description: "Bloco recolhível (accordion)",
    icon: ChevronsUpDown,
    keywords: ["toggle", "accordion", "details", "expandable"],
    group: "advanced",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).insertContent({
        type: "toggle",
        attrs: { summary: "Clique para expandir" },
        content: [{ type: "paragraph", content: [{ type: "text", text: "Conteúdo oculto…" }] }],
      }).run(),
  },
  {
    key: "tabs",
    title: "Abas",
    description: "Conteúdo em abas reordenáveis",
    icon: PanelTop,
    keywords: ["tabs", "abas", "tab", "aba", "guias"],
    group: "advanced",
    command: ({ editor, range }) =>
      withRange(editor.chain().focus(), range).insertContent({
        type: "tabs",
        attrs: { activeIndex: 0 },
        content: [
          { type: "tabPanel", attrs: { label: "Aba 1", active: true }, content: [{ type: "paragraph" }] },
          { type: "tabPanel", attrs: { label: "Aba 2", active: false }, content: [{ type: "paragraph" }] },
        ],
      }).run(),
  },

  {
    key: "image",
    title: "Imagem por URL",
    description: "Insere imagem via link",
    icon: ImageIcon,
    keywords: ["imagem", "image"],
    group: "advanced",
    command: ({ editor, range }) => {
      const url = window.prompt("URL da imagem:");
      if (!url) return;
      withRange(editor.chain().focus(), range).setImage({ src: url }).run();
    },
  },
  {
    key: "embed",
    title: "Embed URL",
    description: "YouTube, Loom, Figma, PDF",
    icon: Youtube,
    keywords: ["embed", "youtube", "loom", "figma", "video"],
    group: "advanced",
    command: ({ editor, range }) => {
      const url = window.prompt("URL do embed:");
      if (!url) return;
      let src = url;
      let provider = "generic";
      const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
      if (yt) { src = `https://www.youtube.com/embed/${yt[1]}`; provider = "youtube"; }
      else if (url.includes("loom.com/share/")) { src = url.replace("/share/", "/embed/"); provider = "loom"; }
      else if (url.includes("figma.com")) { src = `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}`; provider = "figma"; }
      withRange(editor.chain().focus(), range).insertContent({ type: "embed", attrs: { src, provider } }).run();
    },
  },
  {
    key: "linkToArticle",
    title: "Link para artigo",
    description: "Vincula outro artigo da base (wiki-link)",
    icon: Link2,
    keywords: ["link", "artigo", "wiki", "hotlink", "pagina", "referencia"],
    group: "advanced",
    command: ({ editor, range }) => {
      // Remove o "/consulta" (quando vindo do slash menu) antes de abrir picker.
      withRange(editor.chain().focus(), range).run();
      openPageLinkPicker((page) => {
        const title = page.title || "Artigo";
        editor
          .chain()
          .focus()
          .insertContent({
            type: "text",
            text: title,
            marks: [
              {
                type: "kbPageLink",
                attrs: {
                  pageId: page.page_id,
                  spaceId: page.space_id,
                  snapshotTitle: title,
                },
              },
            ],
          })
          .insertContent(" ")
          .run();
      });
    },
  },
];

/** Rótulos amigáveis por grupo (usados no header de cada seção). */
export const BLOCK_GROUP_LABELS: Record<BlockGroup, string> = {
  basic: "Blocos básicos",
  advanced: "Blocos avançados",
};
